/**
 * ModalManager - A reusable modal component for the 3D environment
 * Follows Enteroggi brand design system
 */
import { ModalLifecycleOwner } from './modalLifecycleOwner';
import { createModalFrame } from './modalDom';
import { alertContent, loadingContent } from './modalPresets';
import { installModalStyles } from './modalStyles';

export class ModalManager {
  constructor() {
    this.lifecycle = new ModalLifecycleOwner();
    this.modalStack = this.lifecycle.stack;
    this.injectStyles();
  }

  get activeModal() {
    return this.lifecycle.active;
  }

  /**
   * Inject modal CSS styles into the document
   */
  injectStyles() {
    const cssText = `
      /* Modal Overlay */
      .oggi-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: auto;
      }

      .oggi-modal-overlay.visible {
        opacity: 1;
      }

      /* Modal Container */
      .oggi-modal {
        background: linear-gradient(135deg, #fcf5ff, #fff5fc);
        border-radius: 24px;
        box-shadow: 0 10px 40px -10px rgba(153, 0, 230, 0.3);
        border: 1px solid rgba(153, 0, 230, 0.1);
        max-width: 90vw;
        max-height: 90vh;
        overflow: hidden;
        transform: translateY(20px) scale(0.95);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: 'Sora', sans-serif;
      }

      .oggi-modal-overlay.visible .oggi-modal {
        transform: translateY(0) scale(1);
      }

      /* Modal Sizes */
      .oggi-modal.size-small {
        width: 400px;
      }

      .oggi-modal.size-medium {
        width: 560px;
      }

      .oggi-modal.size-large {
        width: 720px;
      }

      .oggi-modal.size-auto {
        width: auto;
        min-width: 320px;
      }

      /* Modal Header */
      .oggi-modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.5rem 2rem;
        background: linear-gradient(135deg, #9900e6, #ff4dc4, #ff8833);
        position: relative;
        overflow: hidden;
      }

      .oggi-modal-header::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -50%;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle, rgba(255, 77, 196, 0.4) 0%, transparent 70%);
        opacity: 0.5;
      }

      .oggi-modal-header.no-gradient {
        background: #fff;
        border-bottom: 1px solid #e5e7eb;
      }

      .oggi-modal-header.no-gradient .oggi-modal-title {
        color: #111827;
      }

      .oggi-modal-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #fff;
        margin: 0;
        position: relative;
        z-index: 1;
      }

      .oggi-modal-close {
        position: relative;
        z-index: 1;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: rgba(255, 255, 255, 0.2);
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
      }

      .oggi-modal-header.no-gradient .oggi-modal-close {
        background: rgba(0, 0, 0, 0.05);
        color: #6b7280;
      }

      .oggi-modal-close:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: rotate(90deg);
      }

      .oggi-modal-header.no-gradient .oggi-modal-close:hover {
        background: rgba(0, 0, 0, 0.1);
      }

      .oggi-modal-close svg {
        width: 20px;
        height: 20px;
      }

      /* Modal Body */
      .oggi-modal-body {
        padding: 2rem;
        overflow-y: auto;
        max-height: calc(90vh - 160px);
        color: #111827;
      }

      .oggi-modal-body p {
        margin: 0 0 1rem;
        line-height: 1.6;
        color: #6b7280;
      }

      .oggi-modal-body p:last-child {
        margin-bottom: 0;
      }

      /* Modal Footer */
      .oggi-modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 1rem;
        padding: 1.5rem 2rem;
        border-top: 1px solid #e5e7eb;
        background: #fff;
      }

      .oggi-modal-footer.center {
        justify-content: center;
      }

      .oggi-modal-footer.space-between {
        justify-content: space-between;
      }

      /* Modal Buttons */
      .oggi-modal-btn {
        padding: 0.75rem 1.5rem;
        font-size: 0.95rem;
        font-weight: 500;
        font-family: 'Sora', sans-serif;
        border-radius: 9999px;
        border: none;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
      }

      .oggi-modal-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .oggi-modal-btn.btn-primary {
        background: #9900e6;
        color: #fff;
        box-shadow: 0 4px 15px -3px rgba(153, 0, 230, 0.3);
      }

      .oggi-modal-btn.btn-primary:hover:not(:disabled) {
        background: hsl(280 100% 35%);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px -3px rgba(153, 0, 230, 0.4);
      }

      .oggi-modal-btn.btn-secondary {
        background: #fff;
        color: #6b7280;
        border: 1px solid #e5e7eb;
      }

      .oggi-modal-btn.btn-secondary:hover:not(:disabled) {
        background: #f9fafb;
        border-color: #d1d5db;
      }

      .oggi-modal-btn.btn-danger {
        background: #ef4444;
        color: #fff;
        box-shadow: 0 4px 15px -3px rgba(239, 68, 68, 0.3);
      }

      .oggi-modal-btn.btn-danger:hover:not(:disabled) {
        background: #dc2626;
        transform: translateY(-2px);
      }

      .oggi-modal-btn.btn-success {
        background: #10b981;
        color: #fff;
        box-shadow: 0 4px 15px -3px rgba(16, 185, 129, 0.3);
      }

      .oggi-modal-btn.btn-success:hover:not(:disabled) {
        background: #059669;
        transform: translateY(-2px);
      }

      .oggi-modal-btn.btn-ghost {
        background: transparent;
        color: #6b7280;
      }

      .oggi-modal-btn.btn-ghost:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.05);
      }

      /* Form Elements in Modal */
      .oggi-modal-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1rem;
        font-family: 'Sora', sans-serif;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        outline: none;
        transition: all 0.2s ease;
        background: #fff;
      }

      .oggi-modal-input:focus {
        border-color: #9900e6;
        box-shadow: 0 0 0 3px rgba(153, 0, 230, 0.15);
      }

      .oggi-modal-input::placeholder {
        color: #9ca3af;
      }

      .oggi-modal-label {
        display: block;
        font-size: 0.875rem;
        font-weight: 500;
        color: #6b7280;
        margin-bottom: 0.5rem;
      }

      .oggi-modal-form-group {
        margin-bottom: 1.5rem;
      }

      .oggi-modal-form-group:last-child {
        margin-bottom: 0;
      }

      /* Modal Variants */
      .oggi-modal.variant-info .oggi-modal-header {
        background: linear-gradient(135deg, #3b82f6, #60a5fa);
      }

      .oggi-modal.variant-success .oggi-modal-header {
        background: linear-gradient(135deg, #10b981, #34d399);
      }

      .oggi-modal.variant-warning .oggi-modal-header {
        background: linear-gradient(135deg, #f59e0b, #fbbf24);
      }

      .oggi-modal.variant-danger .oggi-modal-header {
        background: linear-gradient(135deg, #ef4444, #f87171);
      }

      /* Icon Container */
      .oggi-modal-icon {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;
      }

      .oggi-modal-icon.icon-primary {
        background: rgba(153, 0, 230, 0.1);
        color: #9900e6;
      }

      .oggi-modal-icon.icon-success {
        background: rgba(16, 185, 129, 0.1);
        color: #10b981;
      }

      .oggi-modal-icon.icon-warning {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }

      .oggi-modal-icon.icon-danger {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
      }

      .oggi-modal-icon.icon-info {
        background: rgba(59, 130, 246, 0.1);
        color: #3b82f6;
      }

      .oggi-modal-icon svg {
        width: 32px;
        height: 32px;
      }

      /* Centered Content */
      .oggi-modal-body.centered {
        text-align: center;
      }

      .oggi-modal-body.centered p {
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }

      /* List Styles */
      .oggi-modal-list {
        list-style: none;
        padding: 0;
        margin: 1rem 0;
      }

      .oggi-modal-list li {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 0.75rem 0;
        border-bottom: 1px solid #f3f4f6;
        color: #374151;
      }

      .oggi-modal-list li:last-child {
        border-bottom: none;
      }

      .oggi-modal-list-icon {
        width: 20px;
        height: 20px;
        color: #9900e6;
        flex-shrink: 0;
        margin-top: 2px;
      }

      /* Loading State */
      .oggi-modal-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem 2rem;
      }

      .oggi-modal-spinner {
        width: 48px;
        height: 48px;
        border: 4px solid rgba(153, 0, 230, 0.2);
        border-top-color: #9900e6;
        border-radius: 50%;
        animation: oggi-modal-spin 0.8s linear infinite;
        margin-bottom: 1rem;
      }

      @keyframes oggi-modal-spin {
        to { transform: rotate(360deg); }
      }

      /* Dark theme (in-world surfaces) - the dark-glass HUD language from
         designSystem.js TOKENS.panel. Opt in per modal with theme: 'dark';
         the light brand theme above stays the default for lobby/portal use. */
      .oggi-modal.oggi-modal--dark {
        background: rgba(18, 18, 22, 0.96);
        border: 1px solid rgba(255, 255, 255, 0.12);
        box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(8px);
      }

      .oggi-modal--dark .oggi-modal-header,
      .oggi-modal--dark .oggi-modal-header.no-gradient {
        background: transparent;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      }

      .oggi-modal--dark .oggi-modal-header::before {
        display: none;
      }

      .oggi-modal--dark .oggi-modal-title,
      .oggi-modal--dark .oggi-modal-header.no-gradient .oggi-modal-title {
        color: #fff;
      }

      .oggi-modal--dark .oggi-modal-close,
      .oggi-modal--dark .oggi-modal-header.no-gradient .oggi-modal-close {
        background: rgba(255, 255, 255, 0.1);
        color: #ccc;
      }

      .oggi-modal--dark .oggi-modal-close:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      .oggi-modal--dark .oggi-modal-body {
        color: #eee;
      }

      .oggi-modal--dark .oggi-modal-body p {
        color: rgba(255, 255, 255, 0.75);
      }

      .oggi-modal--dark .oggi-modal-footer {
        background: transparent;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
      }

      .oggi-modal--dark .oggi-modal-btn.btn-primary {
        background: #8ab4ff;
        color: #10131a;
        box-shadow: none;
      }

      .oggi-modal--dark .oggi-modal-btn.btn-primary:hover:not(:disabled) {
        background: #aecbff;
        transform: translateY(-1px);
      }

      .oggi-modal--dark .oggi-modal-btn.btn-secondary {
        background: rgba(255, 255, 255, 0.08);
        color: #eee;
        border: 1px solid rgba(255, 255, 255, 0.15);
      }

      .oggi-modal--dark .oggi-modal-btn.btn-secondary:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.25);
      }

      .oggi-modal--dark .oggi-modal-btn.btn-ghost {
        color: rgba(255, 255, 255, 0.6);
      }

      .oggi-modal--dark .oggi-modal-btn.btn-ghost:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.08);
      }

      .oggi-modal--dark .oggi-modal-input {
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.15);
        color: #fff;
      }

      .oggi-modal--dark .oggi-modal-input:focus {
        border-color: #8ab4ff;
        box-shadow: 0 0 0 2px rgba(138, 180, 255, 0.35);
      }

      .oggi-modal--dark .oggi-modal-input::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }

      .oggi-modal--dark .oggi-modal-label {
        color: rgba(255, 255, 255, 0.7);
      }

      .oggi-modal--dark .oggi-modal-list li {
        border-bottom-color: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.85);
      }

      .oggi-modal--dark .oggi-modal-list-icon {
        color: #8ab4ff;
      }

      .oggi-modal--dark .oggi-modal-spinner {
        border-color: rgba(138, 180, 255, 0.25);
        border-top-color: #8ab4ff;
      }

      /* Responsive */
      @media (max-width: 640px) {
        .oggi-modal {
          margin: 1rem;
          max-width: calc(100vw - 2rem);
          border-radius: 16px;
        }

        .oggi-modal.size-small,
        .oggi-modal.size-medium,
        .oggi-modal.size-large {
          width: calc(100vw - 2rem);
        }

        .oggi-modal-header {
          padding: 1.25rem 1.5rem;
        }

        .oggi-modal-body {
          padding: 1.5rem;
        }

        .oggi-modal-footer {
          padding: 1.25rem 1.5rem;
          flex-wrap: wrap;
        }

        .oggi-modal-btn {
          flex: 1;
          min-width: 120px;
        }
      }
    `;
    installModalStyles(cssText);
  }

  /**
   * Create and show a modal
   * @param {Object} [options] - Modal configuration
   * @param {string} [options.title=''] - Modal title
   * @param {string|HTMLElement} [options.content=''] - Modal body content (HTML string or element)
   * @param {string} [options.size='medium'] - Modal size: 'small', 'medium', 'large', 'auto'
   * @param {string} [options.variant='default'] - Modal variant: 'default', 'info', 'success', 'warning', 'danger'
   * @param {string} [options.theme='light'] - 'light' (brand, lobby/portal) or 'dark' (in-world dark glass)
   * @param {boolean} [options.showHeader=true] - Whether to show the header
   * @param {boolean} [options.headerGradient=true] - Whether to show gradient in header
   * @param {boolean} [options.showClose=true] - Whether to show close button
   * @param {boolean} [options.closeOnOverlay=true] - Whether clicking overlay closes modal
   * @param {boolean} [options.closeOnEscape=true] - Whether pressing Escape closes modal
   * @param {Array} [options.buttons] - Array of button configurations
   * @param {Function} [options.onClose] - Callback when modal is closed
   * @param {Function} [options.onOpen] - Callback when modal is opened
   * @returns {Object} Modal instance with methods
   */
  show(options = {}) {
    const {
      title = '',
      content = '',
      size = 'medium',
      variant = 'default',
      theme = 'light',
      showHeader = true,
      headerGradient = true,
      showClose = true,
      closeOnOverlay = true,
      closeOnEscape = true,
      buttons = [],
      onClose = null,
      onOpen = null,
    } = options;

    const { overlay, modal } = createModalFrame({ size, variant, theme });

    // Header
    if (showHeader) {
      const header = document.createElement('div');
      header.className = `oggi-modal-header${headerGradient ? '' : ' no-gradient'}`;

      const titleEl = document.createElement('h3');
      titleEl.className = 'oggi-modal-title';
      titleEl.textContent = title;
      header.appendChild(titleEl);

      if (showClose) {
        const closeBtn = document.createElement('button');
        closeBtn.className = 'oggi-modal-close';
        closeBtn.innerHTML = `
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        `;
        closeBtn.onclick = () => this.close(modalInstance);
        header.appendChild(closeBtn);
      }

      modal.appendChild(header);
    }

    // Body
    const body = document.createElement('div');
    body.className = 'oggi-modal-body';
    if (typeof content === 'string') {
      body.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      body.appendChild(content);
    }
    modal.appendChild(body);

    // Footer with buttons
    if (buttons.length > 0) {
      const footer = document.createElement('div');
      footer.className = 'oggi-modal-footer';

      buttons.forEach((btn) => {
        const button = document.createElement('button');
        button.className = `oggi-modal-btn btn-${btn.type || 'secondary'}`;
        button.textContent = btn.label;
        if (btn.disabled) button.disabled = true;
        button.onclick = () => {
          if (btn.onClick) {
            btn.onClick(modalInstance);
          }
          if (btn.closeOnClick !== false) {
            this.close(modalInstance);
          }
        };
        footer.appendChild(button);
      });

      modal.appendChild(footer);
    }

    overlay.appendChild(modal);

    // Handle overlay click
    if (closeOnOverlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.close(modalInstance);
        }
      });
    }

    // Create modal instance
    const modalInstance = {
      overlay,
      modal,
      body,
      onClose,
      onOpen,
      closeOnEscape,
      isOpen: false,
      openingFrame: null,
      closingTimer: null,
      closing: false,
      closeSettled: false,
    };

    // Add to DOM
    document.body.appendChild(overlay);

    this.lifecycle.register(
      /** @type {import('./modalLifecycleOwner').ModalHandle} */ (
        /** @type {unknown} */ (modalInstance)
      ),
    );
    return modalInstance;
  }

  /**
   * Close a modal
   * @param {Object} modalInstance - The modal instance to close
   */
  close(modalInstance) {
    this.lifecycle.close(modalInstance);
  }

  /**
   * Close all open modals
   */
  closeAll() {
    this.lifecycle.closeAll();
  }

  /** Immediately release every modal and page-lifetime listener. */
  dispose() {
    this.lifecycle.dispose();
  }

  /**
   * Update modal content
   * @param {Object} modalInstance - The modal instance
   * @param {string|HTMLElement} content - New content
   */
  updateContent(modalInstance, content) {
    if (!modalInstance || !modalInstance.body) return;

    if (typeof content === 'string') {
      modalInstance.body.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      modalInstance.body.innerHTML = '';
      modalInstance.body.appendChild(content);
    }
  }

  // ============================================
  // PRESET MODAL TYPES
  // ============================================

  /**
   * Show a confirmation dialog
   * @param {Object} [options] - Configuration
   * @param {string} [options.title='Confirm Action'] - Dialog title
   * @param {string} [options.message] - Confirmation message
   * @param {string} [options.confirmText='Confirm'] - Confirm button text
   * @param {string} [options.cancelText='Cancel'] - Cancel button text
   * @param {string} [options.confirmType='primary'] - Confirm button type
   * @param {string} [options.theme='light'] - 'light' or 'dark'
   * @param {Function} [options.onConfirm] - Callback on confirm
   * @param {Function} [options.onCancel] - Callback on cancel
   */
  confirm(options = {}) {
    const {
      title = 'Confirm Action',
      message = 'Are you sure you want to proceed?',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      confirmType = 'primary',
      theme = 'light',
      onConfirm = null,
      onCancel = null,
    } = options;

    return this.show({
      title,
      content: `<p>${message}</p>`,
      size: 'small',
      theme,
      buttons: [
        {
          label: cancelText,
          type: 'secondary',
          onClick: () => {
            if (onCancel) onCancel();
          },
        },
        {
          label: confirmText,
          type: confirmType,
          onClick: () => {
            if (onConfirm) onConfirm();
          },
        },
      ],
    });
  }

  /**
   * Show an alert/info modal
   * @param {Object} [options] - Configuration
   * @param {string} [options.title='Information'] - Alert title
   * @param {string} [options.message=''] - Alert message
   * @param {string} [options.buttonText='OK'] - Button text
   * @param {string} [options.icon='info'] - Icon type: 'info', 'success', 'warning', 'danger'
   * @param {string} [options.theme='light'] - 'light' or 'dark'
   * @param {Function} [options.onClose] - Callback on close
   */
  alert(options = {}) {
    const {
      title = 'Information',
      message = '',
      buttonText = 'OK',
      icon = 'info',
      theme = 'light',
      onClose = null,
    } = options;

    const content = alertContent(icon, message);

    return this.show({
      title,
      content,
      size: 'small',
      theme,
      headerGradient: false,
      onClose,
      buttons: [
        {
          label: buttonText,
          type: 'primary',
        },
      ],
    });
  }

  /**
   * Show a loading modal
   * @param {Object} [options] - Configuration
   * @param {string} [options.message='Loading...'] - Loading message
   * @param {string} [options.theme='light'] - 'light' or 'dark'
   * @returns {Object} Modal instance (use close() to dismiss)
   */
  loading(options = {}) {
    const { message = 'Loading...', theme = 'light' } = options;

    const content = loadingContent(message);

    return this.show({
      content,
      size: 'small',
      theme,
      showHeader: false,
      showClose: false,
      closeOnOverlay: false,
      closeOnEscape: false,
    });
  }

  /**
   * Show a prompt/input modal
   * @param {Object} [options] - Configuration
   * @param {string} [options.title='Enter Value'] - Prompt title
   * @param {string} [options.message] - Optional message
   * @param {string} [options.placeholder=''] - Input placeholder
   * @param {string} [options.defaultValue=''] - Default input value
   * @param {string} [options.inputType='text'] - Input type
   * @param {string} [options.theme='light'] - 'light' or 'dark'
   * @param {Function} [options.onSubmit] - Callback with input value
   * @param {Function} [options.onCancel] - Callback on cancel
   */
  prompt(options = {}) {
    const {
      title = 'Enter Value',
      message = '',
      placeholder = '',
      defaultValue = '',
      inputType = 'text',
      theme = 'light',
      onSubmit = null,
      onCancel = null,
    } = options;

    const inputId = `oggi-prompt-${Date.now()}`;
    let inputValue = defaultValue;

    const content = document.createElement('div');
    content.innerHTML = `
      ${message ? `<p>${message}</p>` : ''}
      <div class="oggi-modal-form-group">
        <input 
          type="${inputType}" 
          id="${inputId}" 
          class="oggi-modal-input" 
          placeholder="${placeholder}"
          value="${defaultValue}"
        />
      </div>
    `;

    const modal = this.show({
      title,
      content,
      size: 'small',
      theme,
      headerGradient: false,
      buttons: [
        {
          label: 'Cancel',
          type: 'secondary',
          onClick: () => {
            if (onCancel) onCancel();
          },
        },
        {
          label: 'Submit',
          type: 'primary',
          closeOnClick: false,
          onClick: (modalInstance) => {
            const input = /** @type {HTMLInputElement|null} */ (document.getElementById(inputId));
            if (input) {
              inputValue = input.value;
              if (onSubmit) onSubmit(inputValue);
              this.close(modalInstance);
            }
          },
        },
      ],
      onOpen: () => {
        const input = /** @type {HTMLInputElement|null} */ (document.getElementById(inputId));
        if (input) {
          input.focus();
          input.select();
          // Submit on Enter
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              inputValue = input.value;
              if (onSubmit) onSubmit(inputValue);
              this.close(modal);
            }
          });
        }
      },
    });

    return modal;
  }

  /**
   * Show an image modal
   * @param {Object} [options] - Configuration
   * @param {string} [options.src] - Image source URL (the modal is useless without one)
   * @param {string} [options.title] - Optional title
   * @param {string} [options.alt='Image'] - Image alt text
   */
  image(options = {}) {
    const { src, title = '', alt = 'Image' } = options;

    const content = `
      <div style="text-align: center;">
        <img 
          src="${src}" 
          alt="${alt}" 
          style="max-width: 100%; max-height: 70vh; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15);"
        />
      </div>
    `;

    return this.show({
      title,
      content,
      size: 'large',
      showHeader: !!title,
      headerGradient: false,
    });
  }

  /**
   * Show a custom form modal
   * @param {Object} [options] - Configuration
   * @param {string} [options.title='Form'] - Form title
   * @param {Array} [options.fields=[]] - Array of field configurations
   * @param {string} [options.submitText='Submit'] - Submit button label
   * @param {string} [options.theme='light'] - 'light' or 'dark'
   * @param {Function} [options.onSubmit] - Callback with form data
   * @param {Function} [options.onCancel] - Callback on cancel
   */
  form(options = {}) {
    const {
      title = 'Form',
      fields = [],
      submitText = 'Submit',
      theme = 'light',
      onSubmit = null,
      onCancel = null,
    } = options;

    const formId = `oggi-form-${Date.now()}`;

    let formHtml = `<form id="${formId}">`;
    fields.forEach((field, index) => {
      const fieldId = `${formId}-field-${index}`;
      formHtml += `
        <div class="oggi-modal-form-group">
          ${field.label ? `<label class="oggi-modal-label" for="${fieldId}">${field.label}</label>` : ''}
          ${
            field.type === 'textarea'
              ? `<textarea 
                id="${fieldId}" 
                name="${field.name}" 
                class="oggi-modal-input" 
                placeholder="${field.placeholder || ''}"
                rows="${field.rows || 3}"
                ${field.required ? 'required' : ''}
              >${field.value || ''}</textarea>`
              : `<input 
                type="${field.type || 'text'}" 
                id="${fieldId}" 
                name="${field.name}"
                class="oggi-modal-input" 
                placeholder="${field.placeholder || ''}"
                value="${field.value || ''}"
                ${field.required ? 'required' : ''}
              />`
          }
        </div>
      `;
    });
    formHtml += '</form>';

    const modal = this.show({
      title,
      content: formHtml,
      size: 'medium',
      theme,
      headerGradient: false,
      buttons: [
        {
          label: 'Cancel',
          type: 'secondary',
          onClick: () => {
            if (onCancel) onCancel();
          },
        },
        {
          label: submitText,
          type: 'primary',
          closeOnClick: false,
          onClick: (modalInstance) => {
            const form = /** @type {HTMLFormElement|null} */ (document.getElementById(formId));
            if (form && form.checkValidity()) {
              const formData = new FormData(form);
              const data = Object.fromEntries(formData.entries());
              if (onSubmit) onSubmit(data);
              this.close(modalInstance);
            } else if (form) {
              form.reportValidity();
            }
          },
        },
      ],
    });

    return modal;
  }
}

// Create and export a singleton instance
export const modalManager = new ModalManager();
